import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Platform } from 'ionic-angular';
import { ResourceProvider } from '../../providers/resource/resource';
import { APP_CONFIG, Env } from '../../environment';
import { FormControlProvider } from '../../providers/formbuilder/formcontrol';
import { UtilsProvider } from '../../providers/myria/utils-provider';
import { Country } from '../../model/myria/app/country';
import { TranslateService } from '@ngx-translate/core';
import { RmtPopupComponent } from '../rmt-popup/rmt-popup';
import { RouteProvider } from '../../providers/route/route';
import { RegisterProvider } from '../../providers/register/register';
import { AuthProvider } from '../../providers/authorization';
import { LoginProvider } from '../../providers/myria/login-provider';
import * as libphonenumber from 'google-libphonenumber';
import { regions } from '../../environment/region/regions';
import { LoggerEvent, LoggerProvider } from '../../providers/logger/logger';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { PhoneProvider } from '../../providers/phone/phone';

@Component({
  selector: 'rmt-phone',
  templateUrl: 'rmt-phone.html',
  animations: [
    trigger('fade', [
      state('void', style({ opacity: 0 })),
      state('*', style({ opacity: 1 })),
      transition('void <=> *', animate('200ms ease-in-out'))
    ])
  ]
})
export class RmtPhoneComponent {
  @ViewChild('input') input;
  @ViewChild('inputCountry') inputCountry;
  @ViewChild('select') select;
  @Input() form;
  @Input() currencies;
  @Input() formControlName: string;
  @Input() formControlNameCountry: string;
  @Input() combinePhoneAndCountry: boolean = false;
  @Input() checkBlackListed: boolean = true;
  @Input() disableCountry: boolean = false;
  @Input() showArrow: boolean = true;
  @Input() customCountries: string[] = null;
  @Input() phoneCode: boolean = false;
  @Input() pickerId: string = '';
  @Input() phoneInputId: string = '';
  @Input() disabled: boolean = false;
  @Input() newDesign: boolean = false;
  @Input() defaultPhoneCountry = null;
  @Input() defaultPlaceholderNumber ="123 456 7890";
  @Input() isPhone: boolean = false;
  @Output() ionChange = new EventEmitter<any>();
  @Input() formControl ;
  @Input() inputText = null;
  @Input() isActive: boolean = false;
  @Input() hideError: boolean = false;
  @Input() hideLabel: boolean = false;
  @Input() errorMessageKey: string = '';
  @Input() showForcedErrorInput: boolean = false;
  @Input() callBackFunc;
  @Input() phoneLabel: string = this.translate.instant('form.customerProfileForm.mobilePhone.label');
  @Input() allowFormattedPhoneNumber: boolean = false;
  @Input() filterTwilioEnabledCountries: boolean = false;
  @Input() countryLabel: string = this.translate.instant('form.customerProfileForm.phoneNumberCountryCode.label');
  @Input() showListHeader: boolean = false;
  @Input() checkForCountryChange: boolean = true;
  @Output() inputBlurEmitter = new EventEmitter<string>();
  @Input() lazyValidationEnabled: boolean = false;
  @Input() validatePhoneNumber: boolean = true;
  @Output() blurEmitter = new EventEmitter<any>();
  @Input() isEditable: boolean = true;
  @Input() isSignin: boolean = false;
  @Input() inputType="tel";
  @Input() isAirtimeCountryLoading: boolean = false;

  setDefault: boolean = true;
  params;
  phone;
  selectedCountry;
  selectedCountryPrefix;
  changeTimeout;
  formDisableCountry: boolean = false;
  countries: Country[];
  countriesInitialized: boolean = false;
  lastValue;
  isNumberValidating: boolean = false;
  autocomplete;
  showErrorOfBlur: boolean = false;

  phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

  constructor(
    public formProvider: FormControlProvider,
    private utils: UtilsProvider,
    private translate: TranslateService,
    private routeProvider: RouteProvider,
    private resourceProvider: ResourceProvider,
    private platform: Platform,
    private registerProvider: RegisterProvider,
    private authProvider: AuthProvider,
    private loginProvider: LoginProvider,
    private phoneProvider: PhoneProvider,
  ) {
    this.initParams();
    this.autocomplete = this.platform.is('ios') ? "tel" : "tel-national";
  }

  initParams() {
    const getFilteredCountries = async () => {
      const countries = await this.formProvider.getPhoneNumberCountriesRegistration(this.filterTwilioEnabledCountries);
      if (this.isAirtimeCountryLoading) {
        const allCountries = (await this.resourceProvider.getAllCountries()).filter(country => country.IsOfferedDestinationCountry);
        const allCountryCodes = allCountries.map(country => country.CountryCode.trim().replace('-', ''));
        const filteredCountries = countries.filter(country => allCountryCodes.includes(String(country.value)));
        return filteredCountries;
      } else {
        return countries;
      }
    }
    this.params = {
      header: !this.showListHeader ? this.translate.instant('page.registrationStep2Page.selectCountryCode') : this.translate.instant('form.recipientForm.country.countryRegex'),
      placeholder: this.translate.instant('page.languagePage.countrySearch'),
      useIcons: !this.newDesign,
      itemFieldIcon: 'category',
      itemFieldIconPrefix: 'custom-icon-country-',
      itemFieldSelectedText: 'category',
      itemFieldValue: 'category',
      itemsPromise: Promise.resolve(getFilteredCountries()),
      pickerId: this.pickerId, //pass pickerid to selectpickerpage,
      favoritePromise: null,
      onSelect: async(country) => {
        if (country && country.isBlacklistedOTP && this.checkBlackListed)
          RmtPopupComponent.showExceptionWithLogTranslation(this, null, 'page.phoneVerificationPage.countryPhoneNotAvailMessage');
        else {
          const countryValue = country[this.params.itemFieldValue];
          this.select.setValue(countryValue);
          this.routeProvider.pop();
        }
      }
    };
  }

  async initCountries() {
    const countries = await this.resourceProvider.getAllCountries();
    this.countries = JSON.parse(JSON.stringify(countries));
    this.countries.forEach(country => {
      if (country && country.CountryCode) {
        country.CountryCode = country.CountryCode.trim() + "-";
      }
    });
    this.countriesInitialized = true;
  }

  async ngAfterViewInit() {
    this.initParams();
    this.setDefault = false;
    try {
      await this.initCountries();
    } catch (ex) {
      LoggerProvider.error(this, LoggerEvent.InvalidPhoneNumber, 'Failed to initialize countries: ' + (ex && ex.message ? ex.message : ex));
    }
    this.params.itemsPromise.then((countries) => {      
      if (this.customCountries) {
        const indexedCountries: any = {};
        for (let i=0; i < countries.length; i++) {
          const country = countries[i];
          indexedCountries[country.category] = country;
        }

        this.params.items = [];
        this.params.itemsPromise = null;
        for (let i=0; i < this.customCountries.length; i++) {
          const country = this.customCountries[i];
          this.params.items.push(indexedCountries[country]);
        }
      }
    });

    if (this.inputCountry && this.inputCountry.value)
      this.select.setValue(this.inputCountry.value);
    else if (this.defaultPhoneCountry)
      this.select.setValue(this.defaultPhoneCountry);
    else if (!this.selectedCountry) {
      const timeout = window.setTimeout(() => {
        this.setDefault = false;
      }, 100);
      await this.select.setValue(APP_CONFIG.region.defaultCountryFrom);
      window.clearTimeout(timeout);
    }
    if(this.showListHeader){
      this.params.favoritePromise = this.formProvider.getTopCountryListWithCountryCode();
    }
    this.setDefault = true;
  }

  ngOnChanges() {
  const isFocused = (this.input && this.input.inputElemt && document.activeElement === this.input.inputElemt.nativeElement) ? true : false;
   if (this.formControlName && this.form)
   this.formDisableCountry = this.form.controls[this.formControlName].disabled;

   if(this.form.invalid && this.form.touched && !isFocused)
    this.hideError = false;
   else if (!this.hideError && this.form.valid) {
    LoggerProvider.info(this, 'Phone number is now valid');
    this.hideError = true;
   }
  }

  getValue() {
    return this.phone;
  }

  setValue(phone) {
    this.setPhone(phone);
  }

  setPhone(phone) {
    this.phone = phone;
    this.input.setValue(phone);
  }

  setCountry(country) {
    this.selectedCountry = country;
    this.select.setValue(country);
  }

  getCountry() {
    return this.selectedCountry;
  }

  getCountryPrefix() {
    return this.selectedCountryPrefix;
  }

  changePhone(ev) {
    this.isNumberValidating = true;
    this.formControl && this.formControl.markAsTouched();
    if(!this.allowFormattedPhoneNumber){
      ev.value = this.removeNumberSpecialChars(ev.value);
    }
    this.phone = ev.value;
    if (this.changeTimeout)
      window.clearTimeout(this.changeTimeout);
    if (this.form.valid)
      this.hideError = true;
    this.changeTimeout = window.setTimeout(() => {
      this.triggerChange(false);
    }, 200);
  }

  async changeCountry(ev) {
    const previousCountry = this.selectedCountry;
    this.selectedCountry = ev;
    if (this.inputCountry)
      this.inputCountry.value = ev;
    this.triggerChange(true);
  }

  changePhoneCountry(ev) {
    if (this.selectedCountry !== ev.value)
      this.changeCountry(ev.value);
  }

  removePrefix(phoneWithPrefix) {
    let phoneWithoutPrefix = phoneWithPrefix.split(' ').slice(1).join('');
    if (phoneWithoutPrefix == '') {
      return this.removeNumberSpecialChars(phoneWithPrefix)
    }
    return phoneWithoutPrefix;
  }

  async triggerChange(isCountryChanged) {
    try {
      if (this.changeTimeout)
        window.clearTimeout(this.changeTimeout);
      this.changeTimeout = null;
      let prefix = '';
      if(this.countries && this.countriesInitialized) {
        const findSelectedCountry = this.countries.filter(country => country.NameAbbrev === this.selectedCountry)[0]
        if(findSelectedCountry && findSelectedCountry.CountryCode) prefix = findSelectedCountry.CountryCode;
      }
      this.selectedCountryPrefix = prefix;
      this.validateAndParsePhoneNumber(isCountryChanged, prefix);
    } catch (ex) {
      LoggerProvider.error(this, LoggerEvent.InvalidPhoneNumber, 'Error in triggerChange: ' + (ex && ex.message ? ex.message : ex));
    }
  }

  static convertCountryPrefix(phoneCountries, countryPrefix, fallbackCountry) {
    const countries = phoneCountries.filter((country) => (String(country.value) === String(countryPrefix)));
    if (countries && countries.length > 0) {
      if (countries.length > 1 && countries.find((country) => (country.category === fallbackCountry)))
        return fallbackCountry;
      else if (countries[0].value === 1)
        return 'US';
      else if (countries[0].category === 'GB')
        return 'UK';
      else
        return countries[0].category;
    }
    return null;
  }

  focus() {
    this.input.inputElemt.nativeElement.focus();
  }

  checkPreKey(ev) {
    if (ev.key === '.' || ev.key === ',')
      ev.preventDefault();
    else if (this.validateValue(ev)){
      ev.value = this.removeNumberSpecialChars(ev.value);
      this.lastValue = ev.value;
    }
  }

  changePostKey(ev) {
    if (this.lastValue !== undefined && (!this.validateValue(ev) || (ev.data && !ev.value)))
     ev.value = this.lastValue;
    if(this.callBackFunc) {
      this.callBackFunc(ev);
    }
  }

  onFocus(ev) {
    this.focus();
  }

  removeNumberSpecialChars(phoneWithSpecialChars){
    let phoneWithoutSpaces = phoneWithSpecialChars.replace(/[^0-9]*/g, '');
    return phoneWithoutSpaces;
  }

  validateValue(ev) {
    if(ev.value==undefined ){
      return false;
    }

    if (ev.value && ev.value.length === 0)
      return true;

    let reg = new RegExp('^[0-9]+$');
    if(this.allowFormattedPhoneNumber === true){
      let cleanValue = this.utils.cleanPhoneNumber(ev.value);
      return reg.test(cleanValue);
    }
    const result = reg.test(ev.value);
    return result;
  }

  checkError() {
    return this.form.control && this.form.control.touched && !this.form.control.valid;
  }

  onBlur(ev) {
    if(!ev.value && this.form.controls && this.form.controls[this.formControlName]) {
      this.form.controls[this.formControlName].markAsTouched();
      this.form.controls.setErrors({'incorrect': true});
      this.form.controls[this.formControlName].setErrors({'incorrect': true});
    }else if(ev.value){
      this.inputBlurEmitter.emit(this.form.controls[this.formControlName]);
    }
    if(this.blurEmitter)
      this.blurEmitter.emit(ev.target);
  }

  getDynamicClass() {
    if (!this.hideError ||
      (this.form &&
      this.form.controls &&
      this.form.controls[this.formControlName] &&
      this.form.controls[this.formControlName].touched &&
      !this.form.controls[this.formControlName].valid)) {
      return 'rmt-phone-fields-error'
    }
    else if (this.disabled) {
      return 'rmt-phone-fields-disabled'
    } else {
      return '';
    }
  }

  getErrorMessageKey() {
    if(this.errorMessageKey)
      return this.errorMessageKey;

    return 'page.phoneVerificationPage.mobilePhoneNumberRegex';
  }

  /**
   * Validates and updates the phone number and country code to keep them
   * in sync. Also, formats the number to remove any special characters or extra digits.
   */
  private validateAndParsePhoneNumber(isCountryChanged: boolean, prefix: string) {
    try {
      // let isValid = false

      const numberToParse = this.resolveNumberToParse();

      if (numberToParse.length < 1) {
        return;
      }
      if (this.validatePhoneNumber) {
        const phoneParam = {
          phone: this.phone,
          selectedCountry: this.selectedCountry,
          numberToParse: numberToParse,
          setPhone: (phone) => this.setPhone(phone),
          setSelectedCountryFromParsedNumber: (parsedNumber) => this.setSelectedCountryFromParsedNumber(parsedNumber),
          setCountry: null,
        };

        const { isValid, isCountryChangedPhone } = this.phoneProvider.validateAndParsePhoneNumber(isCountryChanged, prefix, phoneParam);

        this.hideError = isValid;
        this.ionChange.emit({
          id: this.formControlName,
          isValid: isValid,
          phone: this.phone,
          country: this.selectedCountry,
          prefix: prefix,
          isCountryChanged: isCountryChangedPhone
        });
      } else {
        this.ionChange.emit({
          id: this.formControlName,
          isValid: true,
          phone: this.phone,
          country: this.selectedCountry,
          prefix: prefix,
          isCountryChanged: false
        });
      }
    } catch(error) {
      LoggerProvider.error(this, LoggerEvent.InvalidPhoneNumber, (error as Error).message);
      this.hideError = false;
    }
  }

  private resolveNumberToParse(): string {
    // this.phone can be undefined at initialization, default to the value in the input itself or to an empty string
    return this.phone ? this.phone :
      (this.input && this.input.formControl && this.input.formControl.value ? this.input.formControl.value : "");
  }

  private setSelectedCountryFromParsedNumber(parsedNumber: any) {
    const regionCode = this.phoneUtil.getRegionCodeForNumber(parsedNumber);
    this.selectedCountry = regionCode === "GB" ? "UK" : regionCode;
    this.setCountry(this.selectedCountry);
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-phone/rmt-phone.ts